import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, List, } from "@mui/material";
import { Fragment } from "react";
import { useContent } from "../../../logic";
import { useParams } from "react-router-dom";
import { PortalUpload } from "../../../types";
import { toTitleCase } from "../../../utils";
import { LinkWithSearchParams } from "../LinkWithSearchParams";
import { sortByDate, sortByName } from "../../../utils/sortBy";
import { UploadFileItem } from "../ClosingDocumentsSection";
import { LoadingButton } from "@mui/lab";
import { Refresh } from "@mui/icons-material";
import { useGetNotification, useGetUploadsForNotificationAndUserOrderKey } from "../../../logic/useApiQuery";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

export function DownloadUploadsListDialog({
  onClose,
  notificationId,
}: {
  onClose: Function;
  notificationId?: string;
}) {

  const { closingId = '', id: paramNotificationId = '' } = useParams();
  const finalNotificationId = notificationId || paramNotificationId;
  const { search } = useLocation();

  const POLL_INTERVAL = 60000;
  const REFETCH_INTERVAL = 5000; //Will only start this timer after the previous fetch is complete.
  const [polling, setPolling] = useState(false);


  const {
    data: notification,
  } = useGetNotification( { notificationId: finalNotificationId, queryOptions: { enabled: !!closingId && !!finalNotificationId } })

  const {
    data: uploadsList = [],
    refetch: refreshList,
    isFetching: loadingList,
  } = useGetUploadsForNotificationAndUserOrderKey(
    closingId,
    finalNotificationId,
    search,
    {
      enabled: !!closingId && !!finalNotificationId,
      refetchInterval: () => {
        return polling ? REFETCH_INTERVAL : false;
      },
    }
  )

  const uploads = uploadsList

  const { TaskText, DocumentsText, ClosingText } = useContent();

  const notificationTitle = notification?.Title || notification?.Form?.DisplayName || "";
  const title = notificationTitle ? `${notificationTitle} - Documents` : `${toTitleCase(TaskText)} Documents`;

  const sortedUploads: PortalUpload[] = uploads
    .sort((a: PortalUpload, b: PortalUpload) => {
      const dateSort = sortByDate(a.UploadDate, b.UploadDate);
      if (dateSort) return dateSort;
      return sortByName(a.OriginalDocumentName, b.OriginalDocumentName);
    });

  const hasDocs = !!sortedUploads.length;

  useEffect(() => {
    setPolling(true);
    const timeout = setTimeout(() => {
      setPolling(false);
    }, POLL_INTERVAL);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <Dialog key="uploadsList" open={true} onClose={() => onClose()}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <List sx={{ pt: 0 }}>
          {hasDocs &&
            sortedUploads.map((upload, i) => {
              return (
                <Fragment key={i}>
                  <UploadFileItem
                    upload={upload}
                    CompanyIdOrderIdHash={closingId}
                    notification={notification}
                  />
                  <Divider light={true} />
                </Fragment>
              );
            })}
        </List>

        {!hasDocs && (
          <>
            <DialogContentText>
              No documents found for this {TaskText}.
            </DialogContentText>
            <DialogContentText sx={{ mt: 2 }}>
              {
                loadingList || polling
                  ? (<>{`Refreshing ${DocumentsText} list... `} <CircularProgress size={15} /></>)
                  : `Click "Refresh ${toTitleCase(DocumentsText)}" to find recent completions.`
              }
            </DialogContentText>
          </>
        )}

        <DialogContentText
          variant="body2"
          sx={{ "&.MuiTypography-root": { mt: 3 } }}
        >
          All documents uploaded for this {ClosingText} can be found in your{" "}
          <LinkWithSearchParams
            to={`/closings/${closingId}/closing-documents`}
            newSearchParams={{ component: "uploads" }}
            onClick={() => onClose()}
          >
            {toTitleCase(DocumentsText)}
          </LinkWithSearchParams>
          .
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ m: 1 }}>
        <LoadingButton
          sx={{
            borderRadius: "40px",
          }}
          startIcon={<Refresh />}
          loadingPosition="start"
          loading={loadingList || polling}
          variant="outlined"
          onClick={() => {
            refreshList();
          }}
        >
          {(loadingList || polling) ?
            `Refreshing ${toTitleCase(DocumentsText)}`
            :
            `Refresh ${toTitleCase(DocumentsText)}`
          }
        </LoadingButton>
        <Button
          variant="contained"
          onClick={() => {
            onClose();
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
